import React, { FC, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { CaseStudyPageTransitionDuration } from 'constants/values';
import { RouteBasePaths } from 'constants/values';
import { useUI } from 'hooks/useUI';

import cx from 'classnames';
import BaseButtonInner from 'atoms/BaseButtonInner';
import Video from 'atoms/Video';
import Link from 'next/link';
import CaseStudyGridItemImage from 'atoms/CaseStudyGridItemImage';
import useMousePosition from 'hooks/useMousePosition';
import RichText from 'atoms/RichText';
import Play from 'svgs/Play';
import Pause from 'svgs/Pause';

import * as Type from 'lib/sanity/types';

type Props = {
  slug: string;
  title: string;
  subtitle?: string | null;
  description: Type.RichText;
  images: Type.CaseStudyGridImageItem[];
  video?: Type.VideoField | null;
  isVideoPaused?: boolean;
  isLandingTab?: boolean;
  isFromSnippet?: boolean;
  handleOnClick?: (title: string, subtitle?: string | null) => any;
  aspectRatio?: number;
  snippet?: boolean | null;
  hasDarkBackground?: boolean;
};

export const MouseoverImageDisplay: FC<{
  title: string;
  description: Type.RichText;
  images: Type.CaseStudyGridImageItem[];
  video?: Type.VideoField | null;
  isVideoPaused?: boolean;
  hasDarkBackground: boolean;
  aspectRatio: number;
  snippet?: boolean | null;
  isFromSnippet?: boolean;
}> = ({
  title,
  description,
  images,
  video,
  isVideoPaused,
  hasDarkBackground,
  aspectRatio,
  snippet,
  isFromSnippet,
}) => {
  const gridItemRef = useRef(null);

  // based on the number of images, this function will detect mouseX position on each grid item and decide which index of image should be displayed. ex) if current mouseX is located on the center of image, and there are 3 images in total, 2nd image will be displayed since the mouseX is on 2/3 of image width.
  const mousePosition = useMousePosition(
    gridItemRef.current,
    images.length - 1,
  );
  let mouseX = 0;
  if (typeof mousePosition === 'number' && (video === null || undefined)) {
    mouseX = mousePosition;
  }

  return (
    <div
      className="CaseStudyGridItem flex flex-col gap-content-skinny text-small font-book"
      ref={gridItemRef}
    >
      <div className="relative w-full rounded-none overflow-hidden transition-all duration-500">
        <div
          className="ImageGridBlock__outer-wrapper"
          style={{ paddingTop: `${aspectRatio}%` }}
        >
          {video && video.src ? (
            <Video
              media={video}
              isGridViewVideoPaused={isVideoPaused}
              showPlayPause
            />
          ) : snippet && !!isFromSnippet ? (
            <CaseStudyGridItemImage
              image={images[0]}
              snippet={snippet}
              isFromSnippet={isFromSnippet}
            />
          ) : (
            <CaseStudyGridItemImage image={images[0]} />
          )}
          {/* overlapped image for hover image swapping animation */}
          {(!video || (video && video.src == null)) && (
            <>
              {images[mouseX + 1] && (
                <CaseStudyGridItemImage
                  className="ImageGridBlock__inner-wrapper--hovered"
                  image={images[mouseX + 1]}
                />
              )}
            </>
          )}
        </div>
      </div>
      <div className="space-y-1 w-full max-w-2xl">
        <div
          className={cx({
            'text-gray-01': hasDarkBackground,
            'text-black/60': !hasDarkBackground,
          })}
        >
          <RichText value={description} />
        </div>
      </div>
    </div>
  );
};

const CaseStudyGridItem: FC<Props> = ({
  slug,
  title,
  subtitle,
  description,
  images,
  video,
  isLandingTab,
  isFromSnippet,
  handleOnClick,
  aspectRatio,
  snippet,
  hasDarkBackground,
}) => {
  const gridItemRef = useRef(null);
  const router = useRouter();
  const [isVideoPaused, setIsVideoPaused] = useState(false);

  // based on the number of images, this function will detect mouseX position on each grid item and decide which index of image should be displayed. ex) if current mouseX is located on the center of image, and there are 3 images in total, 2nd image will be displayed since the mouseX is on 2/3 of image width.
  const mousePosition = useMousePosition(
    gridItemRef.current,
    images.length - 1,
  );
  let mouseX = 0;
  if (typeof mousePosition === 'number') {
    mouseX = mousePosition;
  }

  return (
    <div className="relative">
      <Link href={`/${RouteBasePaths.CASE_STUDY}/${slug}`} passHref>
        <a
          onClick={(e) => {
            if (!handleOnClick) {
              return;
            }
            handleOnClick(title, subtitle);
            e.preventDefault();
            setTimeout(() => {
              router.push(`/${RouteBasePaths.CASE_STUDY}/${slug}`);
            }, CaseStudyPageTransitionDuration);
          }}
        >
          <MouseoverImageDisplay
            title={title}
            description={description}
            images={images}
            video={video}
            isVideoPaused={isVideoPaused}
            hasDarkBackground={
              hasDarkBackground !== undefined
                ? hasDarkBackground
                : !!isLandingTab
            }
            aspectRatio={aspectRatio || (9 / 16) * 100}
            snippet={snippet}
            isFromSnippet={isFromSnippet}
          />
        </a>
      </Link>
      {/* Only show play/play controls if a grid view media renders a video */}
      {video && video.src && (
        <div className="absolute top-3 right-3">
          <button
            className="p-1 bg-gray-05/60 hover:bg-gray-05 z-20 rounded-lg"
            onClick={() => setIsVideoPaused(!isVideoPaused)}
          >
            {isVideoPaused ? (
              <Play className="w-4 h-4" />
            ) : (
              <Pause className="w-4 h-4" />
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CaseStudyGridItem;
